import type { Metadata } from "next";
import {
  PLATFORM_BILLING_LEGAL_ENTITY,
  PLATFORM_BILLING_PRIVACY_URL,
  PLATFORM_BILLING_REPLY_TO_EMAIL,
} from "@/lib/platform-billing-contract";

export const PAXOM_BILLING_PUBLIC_HOST = "billing.paxom.ai";
export const PAXOM_BILLING_PUBLIC_ORIGIN = `https://${PAXOM_BILLING_PUBLIC_HOST}`;

const PAXOM_BILLING_PASS_THROUGH_PREFIXES = [
  "/tenant/billing",
  "/api",
  "/platform-contracts",
  "/_next",
];

export function hostnameFromPublicHost(
  host: string | null | undefined,
): string {
  if (!host) return "";

  const trimmed = host.trim().toLowerCase();
  if (!trimmed) return "";

  try {
    const url = trimmed.includes("://")
      ? new URL(trimmed)
      : new URL(`https://${trimmed}`);
    return url.hostname.replace(/^www\./, "");
  } catch {
    return trimmed
      .replace(/^https?:\/\//, "")
      .replace(/^www\./, "")
      .split("/")[0]
      .split(":")[0];
  }
}

export function isPaxomBillingPublicHost(
  host: string | null | undefined,
): boolean {
  const hostname = hostnameFromPublicHost(host);
  if (hostname === PAXOM_BILLING_PUBLIC_HOST) return true;
  if (hostname === "paxom-billing.vercel.app") return true;
  return (
    hostname.startsWith("paxom-billing-") && hostname.endsWith(".vercel.app")
  );
}

function normalizePathname(pathname: string): string {
  const normalized =
    pathname.split("?")[0]?.split("#")[0]?.replace(/\/+$/, "") ?? "";
  return normalized || "/";
}

export function isPaxomBillingPassThroughPath(pathname: string): boolean {
  const normalized = normalizePathname(pathname);
  if (normalized === "/") return true;

  return PAXOM_BILLING_PASS_THROUGH_PREFIXES.some(
    (prefix) =>
      normalized === prefix || normalized.startsWith(`${prefix}/`),
  );
}

export function paxomBillingHomeUrl(requestUrl: string): string {
  return new URL("/", requestUrl).toString();
}

export function isRestaurantHostBillingPath(pathname: string): boolean {
  const normalized = normalizePathname(pathname);
  return (
    normalized === "/tenant/billing" ||
    normalized.startsWith("/tenant/billing/") ||
    normalized === "/api/tenant/billing" ||
    normalized.startsWith("/api/tenant/billing/")
  );
}

export function paxomBillingCanonicalUrl(requestUrl: string): string {
  const incoming = new URL(requestUrl);
  return `${PAXOM_BILLING_PUBLIC_ORIGIN}${incoming.pathname}${incoming.search}`;
}

export function buildPaxomBillingRootMetadata(): Metadata {
  const title = "Paxom billing";
  const description =
    "Bill restaurants with a private link. They review the 12-month agreement and authorize ACH. Starter $499, Growth $749, Advanced $1,499.";

  return {
    metadataBase: new URL(PAXOM_BILLING_PUBLIC_ORIGIN),
    title: { absolute: title },
    description,
    applicationName: "Paxom",
    authors: [{ name: PLATFORM_BILLING_LEGAL_ENTITY }],
    creator: PLATFORM_BILLING_LEGAL_ENTITY,
    publisher: PLATFORM_BILLING_LEGAL_ENTITY,
    robots: {
      index: true,
      follow: true,
    },
    openGraph: {
      type: "website",
      locale: "en_US",
      url: PAXOM_BILLING_PUBLIC_ORIGIN,
      siteName: "Paxom",
      title,
      description,
    },
    twitter: {
      card: "summary",
      title,
      description,
    },
    icons: {
      icon: [{ url: "/paxom-billing-icon.svg", type: "image/svg+xml" }],
    },
    manifest: null,
    alternates: {
      canonical: PAXOM_BILLING_PUBLIC_ORIGIN,
    },
    other: {
      "contact:email": PLATFORM_BILLING_REPLY_TO_EMAIL,
      "privacy": PLATFORM_BILLING_PRIVACY_URL,
    },
  };
}
